import React, { useState, useMemo, useCallback, useRef } from "react";
import Footer from '../Footer';
import "./Usluge.css";

const SERVICE_CATEGORIES = {
  preventiva: {
    id: 'preventiva',
    title: "Preventiva i zaštita",
    icon: "🛡️",
    description: "Redovite preventivne mjere za održavanje optimalnog zdravlja vašeg ljubimca",
    services: [
      "Cijepljenje (zakonsko protiv bjesnoće)",
      "Cijepljenje pasa prema preporukama",
      "Cijepljenje mačaka prema preporukama (uključujući FeLV)",
      "Zaštita protiv ektoparazita (krpelji, buhe, itd.)",
      "Zaštita protiv endoparazita",
      "Redoviti veterinarski pregledi",
    ],
  },
  dijagnostika: {
    id: 'dijagnostika',
    title: "Dijagnostika i internistička medicina",
    icon: "🔍",
    description: "Sveobuhvatna dijagnostika za rano otkrivanje i praćenje bolesti",
    services: [
      "Detaljna procjena unutarnjih organa i zdravstvenog statusa",
      "Anamneza i klinički pregled",
      "Hematološke i biokemijske pretrage krvi",
      "Pretraga urina i stolice",
      "Ultrazvuk abdomena (trbušne šupljine)",
      "Rendgen (RTG) snimke",
      "Kardiološki pregled (EKG, UZV srca, krvni tlak)",
    ],
    details: {
      title: "Detalji dijagnostičkih metoda:",
      items: [
        {
          term: "Ultrazvuk abdomena",
          description: "Neinvazivna metoda za pregled organa trbušne šupljine u realnom vremenu."
        },
        {
          term: "RTG (Rendgen)",
          description: "Neinvazivna metoda koja koristi X-zrake za prikaz kostiju i mekih tkiva."
        },
        {
          term: "Kardiološki pregled",
          description: "Kompletan pregled srčanog zdravlja uključujući EKG, UZV srca i mjerenje krvnog tlaka."
        }
      ]
    }
  },
  sistemski: {
    id: 'sistemski',
    title: "Sistematski pregledi",
    icon: "📋",
    description: "Kompletan zdravstveni pregled prilagođen dobi i vrsti ljubimca",
    services: [
      "Psi do 7 godina - opći klinički pregled, KKS, biokemija, UZV abdomena",
      "Psi od 7 godina - prošireni pregled s EKG-om i RTG toraksa",
      "Mačke do 7 godina - opći pregled, KKS, biokemija, RTG",
      "Mačke od 7 godina - sveobuhvatan pregled s UZV-om abdomena i T4 testom",
    ],
    details: {
      title: "Detalji sistematskih pregleda:",
      description: "Svi sistematski pregledi uključuju zaključno internističko mišljenje i preporuke za daljnju skrb."
    }
  },
  specijalisticki: {
    id: 'specijalisticki',
    title: "Specijalistički pregledi",
    icon: "⭐",
    description: "Pregledi od strane specijaliziranih veterinara",
    services: [
      "Oftalmološki pregled (uključujući tonometriju)",
      "Dermatološki pregled",
      "Liječenje malih sisavaca",
    ],
  },
  kirurgija: {
    id: 'kirurgija',
    title: "Kirurgija i stomatologija",
    icon: "⚕️",
    description: "Kirurški zahvati i dentalna njega",
    services: [
      "Kirurgija mekih tkiva",
      "Stomatološki tretmani",
      "Uklanjanje zubnog kamenca",
      "Ekstrakcije zuba",
      "Liječenje bolesti desni i usne šupljine",
    ],
    details: {
      title: "Stomatološke usluge uključuju:",
      description: "Preventivu, dijagnostiku i liječenje bolesti zuba i usne šupljine. Uključeno uklanjanje kamenca, ekstrakcije zuba te liječenje bolesti desni."
    }
  },
};

const CATEGORIES_WITH_DETAILS = ['dijagnostika', 'sistemski', 'kirurgija'];

export default function Usluge() {
  const [activeCategory, setActiveCategory] = useState("preventiva");
  const contentRef = useRef(null);

  const handleCategoryChange = useCallback((categoryId) => {
    setActiveCategory(categoryId);
    
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  }, []);

  const categoryData = useMemo(() => 
    SERVICE_CATEGORIES[activeCategory], 
    [activeCategory]
  );

  const hasDetails = useMemo(() => 
    CATEGORIES_WITH_DETAILS.includes(activeCategory),
    [activeCategory]
  );

  return (
    <>
      <div className="usluge-container">
        <header className="usluge-header">
          <h1 className="usluge-title">Naše usluge</h1>
          <p className="usluge-subtitle">
            Sveobuhvatna veterinarska skrb za vaše ljubimce
          </p>
        </header>

        <nav className="usluge-categories" aria-label="Kategorije usluga">
          {Object.values(SERVICE_CATEGORIES).map((category) => (
            <button
              key={category.id}
              className={`category-button ${
                activeCategory === category.id ? "active" : ""
              }`}
              onClick={() => handleCategoryChange(category.id)}
              aria-current={activeCategory === category.id ? "page" : undefined}
            >
              <span className="category-icon" aria-hidden="true">
                {category.icon}
              </span>
              <span className="category-name">{category.title}</span>
            </button>
          ))}
        </nav>

        <div ref={contentRef} className="usluge-content">
          <div className="category-card">
            <div className="category-header">
              <div className="category-title-wrapper">
                <span className="category-title-icon" aria-hidden="true">
                  {categoryData.icon}
                </span>
                <h2 className="category-title">{categoryData.title}</h2>
              </div>
              <p className="category-description">{categoryData.description}</p>
            </div>

            <ul className="services-grid">
              {categoryData.services.map((service, index) => (
                <li key={index} className="service-card">
                  <span className="service-icon" aria-hidden="true">✓</span>
                  <span className="service-text">{service}</span>
                </li>
              ))}
            </ul>

            {hasDetails && categoryData.details && (
              <div className="category-details">
                {categoryData.details.title && (
                  <h3>{categoryData.details.title}</h3>
                )}
                {categoryData.details.items ? (
                  <ul>
                    {categoryData.details.items.map((item, index) => (
                      <li key={index}>
                        <strong>{item.term}:</strong> {item.description}
                      </li>
                    ))}
                  </ul>
                ) : categoryData.details.description && (
                  <p>{categoryData.details.description}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}